import { Link, useRouteLoaderData, useSubmit } from "react-router-dom";
//--------------------------------------------------------------------------------------------
import UserInfo from "src/components/common/userInfo";
import { testerEmails } from "src/components/common/functions";
import { Opportunity } from "./types";
import { useAuthStore, User } from "../main/authStore";
//--------------------------------------------------------------------------------------------
type FieldDef = {
    label: string;
    value?: React.ReactNode;
    show?: boolean;
};

export default function OpportunityDetail() {
    const user = useAuthStore(state => state.user) as User;
    const opportunity = useRouteLoaderData<Opportunity>("opportunity")!;
    const submit = useSubmit();
    const handleDelete = async (id: number) => {
        if(!window.confirm("Are you sure to delete this opportunity?\n\nThis action cannot be undone.")) return;
        const formData = new FormData();
        formData.append("id", String(id));
        submit(formData, {
            method: "post",
            action: "Delete"
        });
    };
    const handleValidate = (id:number) => {
        if(!window.confirm("Are you sure to validate this opportunity?")) return;
        let validateReason = window.prompt("reason to validdate:");
        if(validateReason === null) return;
        while(validateReason === ""){
            window.alert("Reason is required to validate.");
            validateReason = window.prompt("reason to validate:");
            if(validateReason === null) return;
        }
        const formData = new FormData();
        formData.append("validateReason", validateReason);
        formData.append("id", String(id));
        submit(formData, {
            method: "post",
            action: "Validate"
        });
    }
    const handleVoid = (id: number) => {
        if(!window.confirm("Are you sure to void this opportunity?")) return;
        let voidReason = window.prompt("reason to void:");
        if(voidReason === null) return;
        while(voidReason === ""){
            window.alert("Reason is required to void.");
            voidReason = window.prompt("reason to void:");
            if(voidReason === null) return;
        }
        const formData = new FormData();
        formData.append("voidReason", voidReason);
        formData.append("id", String(id));
        submit(formData, {
            method: "post",
            action: "Void"
        });
    }
    const fields: FieldDef[] = [
        { label: "Opportunity Id", value: opportunity.id },
        { label: "Opportunity Name", value: opportunity.opportunityName },
        { label: "Organization Name", value: <Link to = {`/Organizations/${opportunity.organizationId}`}>{opportunity.organizationName}</Link> },
        { label: "Probability", value: opportunity.probability },
        { label: "Bid Type", value: opportunity.bidType },
        { label: "Bid Amount", value: opportunity.bidAmount },
        { label: "Category", value: opportunity.category },
        { label: "State", value: opportunity.state },
        { label: "Location", value: opportunity.location ? <a href = {`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(opportunity.location)}`} target = "_blank" rel="noreferrer">{opportunity.location}</a> : null},
        { label: "Project Name", value: opportunity.projectId !== null ? <Link to = {`/Projects/${opportunity.projectId}`}>{opportunity.projectName}</Link>: null },
        { label: "Actual Close Date", value: opportunity.actualCloseDate },
        {
            label: "Creator",
            show: !!opportunity.creatorId,
            value: (
                <>
                    <UserInfo
                        name={opportunity.creatorName}
                        id={opportunity.creatorId}
                    />
                    <span className="ms-2 text-muted">
                        {opportunity.createdAt}
                    </span>
                </>
            )
        },
        {
            label: "Updater",
            show: !!opportunity.updaterId,
            value: (
                <>
                    <UserInfo
                        name={opportunity.updaterName}
                        id={opportunity.updaterId}
                    />
                    <span className="ms-2 text-muted">
                        {opportunity.updatedAt}
                    </span>
                </>
            )
        },
        {
            label: "Void Reason",
            show: !!opportunity.voidReason,
            value: opportunity.voidReason
        },
        {
            label: "Validation Reason",
            show: !!opportunity.validateReason,
            value: opportunity.validateReason
        }
    ];
    // ------------------------------------------------------------------
    return (
        <div>
            <div className="cssSeperateWhenWide ps-3 pt-3">
                <table className="table table-fixed flex-grow-1 maxWidth-50 me-3">
                    <tbody className="noTableTopBorder">
                        {fields
                        .filter(f => f.value !== null && f.show !== false)
                        .map((f, idx) => (
                            <tr key={idx}>
                                <th>{f.label}</th>
                                <td>{f.value}</td>
                            </tr>
                        ))}
                        {opportunity.contacts.length > 0 && (
                            <tr>
                                <th>Contacts</th>
                                <td className="position-relative">
                                    {opportunity.contacts.slice(0, 5).map(contact => (
                                        <li key={contact.value}>
                                            <Link to={`/Contacts/${String(contact.value)}`}>
                                                {contact.label}
                                            </Link>
                                        </li>
                                    ))}
                                    {opportunity.contacts.length > 5? 
                                    <li><Link to = {`/Contacts?opportunityId=${opportunity.id}`} className="text-warning">More...</Link></li>
                                    :null}
                                </td>
                            </tr>
                        )}
                        {opportunity.users.length > 0 && (
                            <tr>
                                <th>User Responsibles</th>
                                <td className="position-relative">
                                    {opportunity.users.slice(0, 5).map(user => (
                                        <li key={user.value}>
                                            <Link to={`/Users/${String(user.value)}`}>
                                                {user.label}
                                            </Link>
                                        </li>
                                    ))}
                                    {opportunity.users.length > 5? 
                                    <li><Link to = {`/Users?opportunityId=${opportunity.id}`} className="text-warning">More...</Link></li>
                                    :null}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                <div className="d-flex flex-column flex-grow-1 maxWidth-50 me-3">
                    <fieldset className="border rounded flex-grow-1 px-3">
                        <legend className="fw-bold">
                            Background
                        </legend>
                        <span className = "cssNotes">
                            {opportunity.background}
                        </span>
                    </fieldset>
                </div>
            </div>
            <div className="my-3">
            </div>
            <div className="my-3">
                {opportunity.void === "yes"?<>
                    {testerEmails.includes(user.email)?
                    <button
                        children = "Validate"
                        className = "btn btn-primary"
                        onClick = {() => handleValidate(opportunity.id)}
                    />:null}
                </>:
                <>
                <Link
                    children = "Edit"
                    to = "Edit"
                    className = "btn btn-outline-primary ms-3"
                />
                <button
                    children = "Void"
                    type = "button"
                    className = "btn btn-danger ms-3"
                    onClick = {() => handleVoid(opportunity.id)}
                />
                </>}
                {opportunity.void === "yes"?
                <button
                    children = "Delete"
                    className = "btn btn-danger ms-3"
                    onClick = {() => handleDelete(opportunity.id)}
                />:null}
                <Link
                    children = "Back"
                    to = ".."
                    relative = "path"
                    className = "btn btn-outline-secondary float-end"
                />
            </div>
        </div>
    );
}